import { MobileConfig } from './mobile-config.js';

/**
 * MobileManager - Centralized management of all mobile-specific functionality
 */
export class MobileManager {
  constructor() {
    this.initialized = false;
    this.listeners = new Map();
    
    // Component references (will be populated during init)
    this.components = {
      explorerDrawer: null,
      terminalDrawer: null,
      explorerComponent: null,
      terminalComponent: null,
      navToggle: null,
      terminalToggle: null,
      clearButton: null
    };
    
    // Binding methods
    this.fixButtonState = this.fixButtonState.bind(this);
    this.handleTouchEnd = this.handleTouchEnd.bind(this);
    this.handleViewportResize = this.handleViewportResize.bind(this);
    this.handleExplorerClick = this.handleExplorerClick.bind(this);
  }

  /**
   * Initialize all mobile functionality
   */
  init() {
    if (this.initialized) return;
    
    // Cache element references
    this.cacheElements();
    
    // Only proceed if we're on mobile or components need mobile fixes
    if (MobileConfig.utils.isMobile() || MobileConfig.features.enableMobileButtonFix) {
      this.initButtonStateFixes();
    }
    
    if (MobileConfig.features.enableViewportFix) {
      this.initViewportFix();
    }
    
    if (MobileConfig.features.enableMobileNavAutoClose) {
      this.initMobileNavClose();
    }
    
    this.initialized = true;
    console.log('Mobile manager initialized');
    
    // Add window resize listener
    window.addEventListener('resize', this.debouncedResize.bind(this));
    
    return this;
  }
  
  /**
   * Clean up all event listeners
   */
  destroy() {
    if (!this.initialized) return;
    
    // Remove all registered event listeners
    this.listeners.forEach((config, element) => {
      element.removeEventListener(config.event, config.handler);
    });
    
    // Remove window event listeners
    window.removeEventListener('resize', this.debouncedResize);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.handleViewportResize);
    }
    
    this.initialized = false;
    this.listeners.clear();
    
    return this;
  }
  
  /**
   * Cache DOM element references
   */
  cacheElements() {
    // Navigation elements
    this.components.explorerDrawer = document.getElementById('left-nav-drawer');
    this.components.terminalDrawer = document.getElementById('terminal-drawer');
    this.components.navToggle = document.getElementById('nav-drawer-toggle');
    this.components.terminalToggle = document.getElementById('terminal-toggle');
    
    // Get component references if available
    if (this.components.explorerDrawer && this.components.explorerDrawer.__explorerComponent) {
      this.components.explorerComponent = this.components.explorerDrawer.__explorerComponent;
    }
    
    if (this.components.terminalDrawer && this.components.terminalDrawer.__terminalComponent) {
      this.components.terminalComponent = this.components.terminalDrawer.__terminalComponent;
      this.components.clearButton = this.components.terminalDrawer.querySelector('.terminal__clear-btn');
    }
  }
  
  /**
   * Add event listener with tracking for cleanup
   */
  addTrackedListener(element, event, handler, options = {}) {
    if (!element) return;
    
    element.addEventListener(event, handler, options);
    this.listeners.set(element, { event, handler, options });
  }
  
  /**
   * Initialize fixes for button/hover states on mobile
   */
  initButtonStateFixes() {
    // Fix for clear button
    if (this.components.clearButton) {
      this.setupSpecialClearButtonFix(this.components.clearButton);
    }
    
    // Fix for nav button initial state
    this.fixNavButtonInitialState();
    
    // Add general touch handlers
    document.addEventListener('touchend', this.handleTouchEnd, { passive: false });
    this.listeners.set(document, { 
      event: 'touchend', 
      handler: this.handleTouchEnd, 
      options: { passive: false } 
    });
  }
  
  /**
   * Fix for the clear button that's causing problems
   */
  setupSpecialClearButtonFix(clearButton) {
    if (!clearButton) return;
    
    // Replace the element to remove existing listeners
    clearButton.replaceWith(clearButton.cloneNode(true));
    
    // Get new reference after replacement
    const newClearButton = this.components.terminalDrawer.querySelector('.terminal__clear-btn');
    if (!newClearButton) return;
    
    // Update component reference
    this.components.clearButton = newClearButton;
    
    // Add click handler
    this.addTrackedListener(newClearButton, 'click', (e) => {
      // Find the terminal component
      const terminal = this.components.terminalComponent;
      if (terminal && typeof terminal.reset === 'function') {
        terminal.reset();
      }
      
      // Force immediate visual reset
      this.fixButtonState(newClearButton);
      
      // Prevent event propagation
      e.preventDefault();
      e.stopPropagation();
    });
    
    // Add touchend handler
    this.addTrackedListener(newClearButton, 'touchend', (e) => {
      this.fixButtonState(newClearButton);
    }, { passive: false });
    
    // Extra measure: reset on touchstart with delay
    this.addTrackedListener(newClearButton, 'touchstart', () => {
      setTimeout(() => {
        this.fixButtonState(newClearButton);
      }, MobileConfig.timings.longPressDelay);
    });
  }
  
  /**
   * Fix nav button initial state on mobile
   */
  fixNavButtonInitialState() {
    const navToggle = this.components.navToggle;
    const leftNavDrawer = this.components.explorerDrawer;
    
    if (!navToggle || !leftNavDrawer) return;
    
    // On mobile, drawer should start closed
    if (MobileConfig.utils.isMobile()) {
      leftNavDrawer.classList.remove('open');
      navToggle.classList.remove('active');
      
      // Update component state if available
      if (this.components.explorerComponent) {
        this.components.explorerComponent.isVisible = false;
      }
    }
    
    // Add touchend handler for nav toggle
    this.addTrackedListener(navToggle, 'touchend', (e) => {
      setTimeout(() => {
        // Check drawer state and update button to match
        const isOpen = leftNavDrawer.classList.contains('open');
        if (isOpen) {
          navToggle.classList.add('active');
        } else {
          navToggle.classList.remove('active');
        }
      }, MobileConfig.timings.buttonResetDelay);
    });
  }
  
  /**
   * Handle touchend events for button state fixes
   */
  handleTouchEnd(event) {
    // Find the closest button
    const button = event.target.closest('.header-icon, .terminal__icon, button');
    if (!button) return;
    
    // Skip toggle buttons that manage open panels
    if (button.id === 'nav-drawer-toggle' && 
        this.components.explorerDrawer.classList.contains('open')) {
      return;
    }
    
    if (button.id === 'terminal-toggle' && 
        this.components.terminalDrawer.style.display !== 'none') {
      return;
    }
    
    // For other buttons, remove active state
    this.fixButtonState(button);
  }
  
  /**
   * Fix button state to remove stuck active/hover
   */
  fixButtonState(button) {
    if (!button) return;
    
    setTimeout(() => {
      // Remove classes
      button.classList.remove('active', 'hover');
      
      // Apply temporary inline styles to override any active/hover state
      button.style.backgroundColor = 'transparent';
      button.style.color = 'var(--text-color)';
      
      // Force a reflow
      void button.offsetWidth;
      
      // Remove inline styles after a delay
      setTimeout(() => {
        button.style.backgroundColor = '';
        button.style.color = '';
      }, MobileConfig.timings.visualFeedbackDelay);
    }, MobileConfig.timings.buttonResetDelay);
  }
  
  /**
   * Initialize fix for mobile viewport issues with virtual keyboard
   */
  initViewportFix() {
    if (!window.visualViewport) {
      console.warn('VisualViewport API not supported');
      return;
    }
    
    const body = document.body;
    const header = document.getElementById('slim-header');
    
    if (!body || !header) {
      console.warn('Required elements for viewport fix not found');
      return;
    }
    
    // Store original body height style
    this.originalBodyHeightStyle = body.style.height;
    this.isKeyboardAdjustmentActive = false;
    
    // Add visualViewport resize listener
    this.addTrackedListener(
      window.visualViewport, 
      'resize', 
      this.handleViewportResize
    );
  }
  
  /**
   * Handle viewport resize events (for keyboard appearance)
   */
  handleViewportResize() {
    if (!window.visualViewport) return;
    
    const vv = window.visualViewport;
    const layoutViewportHeight = window.innerHeight;
    const body = document.body;
    
    // Check if keyboard is likely visible
    const keyboardLikelyVisible = (layoutViewportHeight - vv.height) > MobileConfig.thresholds.keyboardDetection;
    
    if (keyboardLikelyVisible) {
      // Keyboard is visible - adjust body height
      body.style.height = `${vv.height}px`;
      this.isKeyboardAdjustmentActive = true;
    } else if (this.isKeyboardAdjustmentActive) {
      // Keyboard is hidden - restore original height
      body.style.height = this.originalBodyHeightStyle || '';
      this.isKeyboardAdjustmentActive = false;
    }
  }
  
  /**
   * Initialize auto-close behavior for mobile navigation drawer
   */
  initMobileNavClose() {
    const leftNavDrawer = this.components.explorerDrawer;
    if (!leftNavDrawer) return;
    
    // Close mobile navigation when a file is clicked
    this.addTrackedListener(leftNavDrawer, 'click', this.handleExplorerClick);
    
    // Add resize handler to close drawer when switching to desktop
    this.handleResizeForNav = this.handleResizeForNav.bind(this);
  }
  
  /**
   * Handle clicks in the explorer
   */
  handleExplorerClick(event) {
    if (!MobileConfig.utils.isMobile()) return;
    
    const isFileItem = event.target.closest('.explorer__file-item');
    
    // Only close if a file was clicked (not a folder)
    if (isFileItem && this.components.explorerComponent) {
      if (typeof this.components.explorerComponent.toggle === 'function') {
        this.components.explorerComponent.toggle(false); // Force close
      }
    }
  }
  
  /**
   * Handle resize events for nav drawer
   */
  handleResizeForNav() {
    const leftNavDrawer = this.components.explorerDrawer;
    
    if (!MobileConfig.utils.isMobile() && 
        leftNavDrawer && 
        leftNavDrawer.classList.contains('open') &&
        this.components.explorerComponent) {
      
      if (typeof this.components.explorerComponent.toggle === 'function') {
        this.components.explorerComponent.toggle(false); // Force close
      }
    }
  }
  
  /**
   * Debounced resize handler
   */
  debouncedResize() {
    if (this._resizeTimer) clearTimeout(this._resizeTimer);
    
    this._resizeTimer = setTimeout(() => {
      this.handleResizeForNav();
    }, 100);
  }
}

// Create singleton instance
const mobileManager = new MobileManager();

/**
 * Initialize all mobile functionality
 */
export function initMobile() {
  return mobileManager.init();
}

/**
 * Clean up mobile event listeners
 */
export function cleanupMobile() {
  return mobileManager.destroy();
} 